import type { LoaderArgs } from "@remix-run/node";
import { json } from "@remix-run/node";
import { useActionData, useLoaderData, useTransition } from "@remix-run/react";
import type { ActionArgs } from "@remix-run/server-runtime";
import { ApiError } from "openapi-typescript-fetch";
import { AuthorizationError } from "remix-auth";
import invariant from "tiny-invariant";
import { LoginPage } from "~/pages/login/LoginPage";
import { createAuthenticationSession } from "~/services/api/auth.server";
import { authenticator, AUTH_STRATEGY } from "~/services/auth.server";
import { destroySession, getSession } from "~/services/session.server";

export const loader = async ({ request }: LoaderArgs) => {
  await authenticator.isAuthenticated(request, {
    successRedirect: "/",
  });

  const session = await getSession(request);
  return json(
    {
      justResetPassword: !!session.get("just_reset_password"),
    },
    {
      headers: session.get("just_reset_password")
        ? { "Set-Cookie": await destroySession(session) }
        : {},
    }
  );
};

export type ActionData =
  | {
      step: "email_password";
      error?: string;
      email?: string;
    }
  | {
      step: "otp";
      error?: string;
      email: string;
      backendSessionId: string;
      userId: string;
      otpType: "totp" | "email";
    };

export async function action({ request }: ActionArgs): Promise<ActionData> {
  const formData = await request.formData();
  const step = formData.get("step");

  if (step === "email_password") {
    const email = formData.get("email");
    try {
      const email = formData.get("email");
      const password = formData.get("password");

      invariant(typeof email === "string");
      invariant(typeof password === "string");

      const session = await createAuthenticationSession(
        request,
        email,
        password
      );

      return {
        step: "otp",
        email: email,
        backendSessionId: session.id,
        otpType: session.otp_type,
        userId: session.user_id,
      };
    } catch (err) {
      if (err instanceof ApiError) {
        return {
          step: "email_password",
          error: err.data.message as string,
          email: email as string,
        };
      }
      throw err;
    }
  }

  if (step === "otp") {
    const baseUrl = new URL(request.url);
    const redirect = decodeURIComponent(baseUrl.searchParams.get("cb") || "/");
    const isInternal = new URL(redirect, baseUrl).hostname === baseUrl.hostname;
    try {
      await authenticator.authenticate(AUTH_STRATEGY, request, {
        successRedirect: isInternal ? redirect : "/",
        context: { formData },
      });
      throw new Error("authenticator.authenticate should throw");
    } catch (err) {
      if (err instanceof AuthorizationError) {
        return {
          step: "otp",
          email: formData.get("email") as any,
          error: err.message,
          backendSessionId: formData.get("backendSessionId") as any,
          otpType: formData.get("otpType") as any,
          userId: formData.get("userId") as any,
        };
      }
      throw err;
    }
  }

  throw new Error("unrecognized step " + step);
}

export default function LoginRoute() {
  const loaderData = useLoaderData<typeof loader>();
  const actionData = useActionData<ActionData>();

  const transition = useTransition();
  const isLoading =
    transition.type === "actionSubmission" ||
    transition.type === "actionRedirect";

  return (
    <LoginPage
      justResetPassword={loaderData.justResetPassword}
      isLoading={isLoading}
      actionData={actionData}
    />
  );
}
